// LT-349 (LT-325 C2): Campaign create + edit page. Single component
// driven by `mode` ('create' | 'edit') so the layout stays in one place.
//
// Field-locking honoured per LT-348 LLD §8: in `active` / `paused` /
// `enrolment_closed` the sequence_id and audience_filter inputs render
// disabled. After a terminal state (completed / force_ended / archived)
// every field is disabled and the form is read-only.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Mic, Save, UserPlus } from 'lucide-react';

import PageShell from '../../components/PageShell';
import {
  Button,
  Card,
  Input,
  Select,
  Textarea,
} from '../../ui';
import WebTestAgentModal from '../../components/WebTestAgentModal';
import ContactPickerModal from '../../components/ContactPickerModal';
import { campaignsAPI, extractCampaignApiError } from '../../services/campaignsAPI';
import { contactListsAPI } from '../../services/contactListsAPI';
import { createStaticListFromContacts, pickedListName } from '../../utils/audienceList';
import type { ContactList } from '../../types/contactList';
import type { Contact } from '../../types/contact';
import type {
  Campaign,
  CampaignCreatePayload,
  CampaignStatus,
  CampaignUpdatePayload,
} from '../../types/campaign';
import {
  CampaignBriefEditor,
  normalizeBriefFromCampaign,
  type CampaignBriefValue,
} from './CampaignAiContext';
import {
  CampaignGoalsKpisEditor,
  normalizeKpis,
  type CampaignKpi,
} from './CampaignGoalsKpis';
import {
  CampaignOutputSchemaEditor,
  normalizeOutputSchema,
} from './CampaignOutputSchema';
import type { CampaignFieldDef } from '../../types/campaign';

export type CampaignFormMode = 'create' | 'edit';

/** Strip empty fields so we don't send empty strings to the API. */
function pruneEmptyBrief(v: CampaignBriefValue): CampaignBriefValue {
  const out: CampaignBriefValue = {};
  if (v.purpose && v.purpose.trim()) out.purpose = v.purpose.trim();
  if (v.target_audience && v.target_audience.trim()) out.target_audience = v.target_audience.trim();
  if (v.call_objectives && v.call_objectives.trim()) out.call_objectives = v.call_objectives.trim();
  if (v.likely_objections && v.likely_objections.length > 0) out.likely_objections = v.likely_objections;
  if (v.scripts && v.scripts.length > 0) out.scripts = v.scripts;
  if (v.additional_notes && v.additional_notes.trim()) out.additional_notes = v.additional_notes.trim();
  return out;
}

/** Spread a brief value onto a CampaignCreate / CampaignUpdate payload. */
function applyBriefToPayload(
  payload: CampaignCreatePayload | CampaignUpdatePayload,
  brief: CampaignBriefValue,
): void {
  const trimmed = pruneEmptyBrief(brief);
  if (trimmed.purpose !== undefined) payload.purpose = trimmed.purpose;
  if (trimmed.target_audience !== undefined) payload.target_audience = trimmed.target_audience;
  if (trimmed.call_objectives !== undefined) payload.call_objectives = trimmed.call_objectives;
  if (trimmed.likely_objections !== undefined) payload.likely_objections = trimmed.likely_objections;
  if (trimmed.scripts !== undefined) payload.scripts = trimmed.scripts;
  if (trimmed.additional_notes !== undefined) payload.additional_notes = trimmed.additional_notes;
}

const TERMINAL_STATUSES: CampaignStatus[] = ['completed', 'force_ended', 'archived'];

/**
 * Editable-field set per LLD §8 PUT contract. Mirrors backend
 * `_EDITABLE_FIELDS_BY_STATUS` so the UI disables before the server
 * 409s and the operator sees the lock as a UI affordance, not an error.
 */
// LT-529 retry policy is editable in every non-terminal status (mirrors
// backend _EDITABLE_FIELDS_BY_STATUS). LT-532 dropped `sequence_id` from
// the form — the implicit per-workspace sequence is auto-attached on create.
// LT-623: output_schema ("data to capture") is editable in every
// non-terminal status (mirrors backend _LT623_OUTPUT_SCHEMA_FIELDS).
const EDITABLE_BY_STATUS: Record<CampaignStatus, Set<string>> = {
  draft: new Set([
    'name', 'description',
    'audience_filter', 'contact_list_id', 'enrolment_closes_at',
    'retry_count', 'retry_delays_days',
    'output_schema',
  ]),
  active: new Set(['name', 'description', 'enrolment_closes_at', 'retry_count', 'retry_delays_days', 'output_schema']),
  paused: new Set(['name', 'description', 'enrolment_closes_at', 'retry_count', 'retry_delays_days', 'output_schema']),
  enrolment_closed: new Set(['name', 'description', 'retry_count', 'retry_delays_days', 'output_schema']),
  completed: new Set(),
  force_ended: new Set(),
  archived: new Set(),
};

const DEFAULT_RETRY_COUNT = 3;
const DEFAULT_RETRY_DELAYS_DAYS = [1, 2, 7];

/** Resize the per-retry delays array to match a new count: truncate from
 * the right when shrinking, extend with the last value (or 1) when growing. */
function resizeRetryDelays(delays: number[], count: number): number[] {
  if (count <= delays.length) return delays.slice(0, count);
  const fill = delays.length > 0 ? delays[delays.length - 1] : 1;
  return [...delays, ...Array(count - delays.length).fill(fill)];
}

interface CampaignFormProps {
  mode: CampaignFormMode;
}

const CampaignFormPage: React.FC<CampaignFormProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { id: campaignId } = useParams<{ id: string }>();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [contactListId, setContactListId] = useState(''); // LT-392
  const [enrolmentClosesAt, setEnrolmentClosesAt] = useState('');
  const [audienceFilterRaw, setAudienceFilterRaw] = useState('');
  // LT-529 / LT-532 — per-campaign retry policy (replaces the Sequence picker).
  const [retryCount, setRetryCount] = useState(DEFAULT_RETRY_COUNT);
  const [retryDelaysDays, setRetryDelaysDays] = useState<number[]>(DEFAULT_RETRY_DELAYS_DAYS);
  // LT-413: switched from ai_context (legacy JSON) to the structured
  // brief shape (CampaignBriefValue). Hydrated from structured columns
  // first; falls back to the bridge-view ai_context for historical data.
  const [brief, setBrief] = useState<CampaignBriefValue>({});
  const [goalText, setGoalText] = useState('');
  const [kpis, setKpis] = useState<CampaignKpi[]>([]);
  // LT-624 — "data to capture" schema.
  const [outputSchema, setOutputSchema] = useState<CampaignFieldDef[]>([]);

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [contactLists, setContactLists] = useState<ContactList[]>([]);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // LT-520 — Test Agent (WebRTC). The campaign's implicit sequence (LT-528)
  // always carries a voice_call_with_retry step, so the button only gates
  // on the save being in-flight; the modal works off the campaign id.
  const [testAgentOpen, setTestAgentOpen] = useState(false);
  const [testAgentCampaignId, setTestAgentCampaignId] = useState<string | null>(null);

  // LT-534 — individual-contact picker for the audience source.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerBusy, setPickerBusy] = useState(false);
  const [pickerError, setPickerError] = useState<string | null>(null);
  // Snapshot of the loaded campaign's editable fields. Used for the
  // header "Test Agent" / "Save & test" label decision — if anything has
  // diverged we save first.
  const initialSnapshotRef = useRef<{
    name: string;
    description: string;
    contactListId: string;
    enrolmentClosesAt: string;
    audienceFilterRaw: string;
    retryCount: number;
    retryDelaysJson: string;
    briefJson: string;
    goalText: string;
    kpisJson: string;
    outputSchemaJson: string;
  } | null>(null);

  // Load the workspace's contact lists + the campaign (edit mode).
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      // LT-392 — fetch the workspace's contact lists so the audience selector
      // can render. Failure is non-fatal (operator can still use the legacy
      // audience_filter path).
      contactListsAPI.list({ limit: 200 }).catch(() => [] as ContactList[]),
      mode === 'edit' && campaignId
        ? campaignsAPI.get(campaignId)
        : Promise.resolve(null),
    ])
      .then(([lists, c]) => {
        if (cancelled) return;
        setContactLists(lists);
        if (c) {
          setCampaign(c);
          setName(c.name);
          setDescription(c.description ?? '');
          setContactListId(c.contact_list_id ?? '');
          setEnrolmentClosesAt(
            c.enrolment_closes_at ? c.enrolment_closes_at.slice(0, 16) : '',
          );
          setAudienceFilterRaw(
            c.audience_filter ? JSON.stringify(c.audience_filter, null, 2) : '',
          );
          const loadedRetryCount = c.retry_count ?? DEFAULT_RETRY_COUNT;
          const loadedRetryDelays = c.retry_delays_days ?? DEFAULT_RETRY_DELAYS_DAYS;
          setRetryCount(loadedRetryCount);
          setRetryDelaysDays(loadedRetryDelays);
          const loadedBrief = normalizeBriefFromCampaign(c);
          const loadedKpis = normalizeKpis(c.kpis);
          const loadedSchema = normalizeOutputSchema(c.output_schema);
          setBrief(loadedBrief);
          setGoalText(c.campaign_goals ?? '');
          setKpis(loadedKpis);
          setOutputSchema(loadedSchema);
          // LT-520: capture a snapshot of the loaded fields so the header
          // button can decide "Save & test" vs "Test Agent".
          initialSnapshotRef.current = {
            name: c.name,
            description: c.description ?? '',
            contactListId: c.contact_list_id ?? '',
            enrolmentClosesAt: c.enrolment_closes_at ? c.enrolment_closes_at.slice(0, 16) : '',
            audienceFilterRaw: c.audience_filter ? JSON.stringify(c.audience_filter, null, 2) : '',
            retryCount: loadedRetryCount,
            retryDelaysJson: JSON.stringify(loadedRetryDelays),
            briefJson: JSON.stringify(loadedBrief),
            goalText: c.campaign_goals ?? '',
            kpisJson: JSON.stringify(loadedKpis),
            outputSchemaJson: JSON.stringify(loadedSchema),
          };
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(extractCampaignApiError(err, 'Failed to load.'));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, campaignId]);

  const editable = useMemo(() => {
    if (mode === 'create') return EDITABLE_BY_STATUS.draft;
    return EDITABLE_BY_STATUS[campaign?.status ?? 'draft'];
  }, [mode, campaign]);

  const isReadOnly = Boolean(mode === 'edit' && campaign && TERMINAL_STATUSES.includes(campaign.status));
  const can = (field: string) => !isReadOnly && editable.has(field);

  // LT-520 — dirty detection drives the header button's "Save & test"
  // vs "Test Agent" label. Compares the live form values to the
  // post-load snapshot. Always dirty in create mode (no snapshot yet).
  const isDirty = useMemo(() => {
    if (mode === 'create') return true;
    const snap = initialSnapshotRef.current;
    if (!snap) return false;
    return (
      name !== snap.name
      || description !== snap.description
      || contactListId !== snap.contactListId
      || enrolmentClosesAt !== snap.enrolmentClosesAt
      || audienceFilterRaw !== snap.audienceFilterRaw
      || retryCount !== snap.retryCount
      || JSON.stringify(retryDelaysDays) !== snap.retryDelaysJson
      || JSON.stringify(brief) !== snap.briefJson
      || goalText !== snap.goalText
      || JSON.stringify(kpis) !== snap.kpisJson
      || JSON.stringify(outputSchema) !== snap.outputSchemaJson
    );
  }, [
    mode, name, description, contactListId,
    enrolmentClosesAt, audienceFilterRaw, retryCount, retryDelaysDays,
    brief, goalText, kpis, outputSchema,
  ]);

  const parseAudienceFilter = useCallback((): Record<string, unknown> | null | undefined => {
    const raw = audienceFilterRaw.trim();
    if (!raw) return mode === 'create' ? undefined : null;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
      throw new Error('Audience filter must be a JSON object.');
    } catch (e) {
      throw new Error(
        e instanceof Error
          ? `Invalid audience filter JSON: ${e.message}`
          : 'Invalid audience filter JSON.',
      );
    }
  }, [audienceFilterRaw, mode]);

  /** Refresh the dirty-detection snapshot after a successful save so the
   * header button flips back to "Test Agent" until the next edit. */
  const refreshSnapshot = useCallback((
    c: Campaign,
    briefValue: CampaignBriefValue,
    kpisValue: CampaignKpi[],
    schemaValue: CampaignFieldDef[],
  ) => {
    initialSnapshotRef.current = {
      name: c.name,
      description: c.description ?? '',
      contactListId: c.contact_list_id ?? '',
      enrolmentClosesAt: c.enrolment_closes_at ? c.enrolment_closes_at.slice(0, 16) : '',
      audienceFilterRaw: c.audience_filter ? JSON.stringify(c.audience_filter, null, 2) : '',
      retryCount: c.retry_count ?? DEFAULT_RETRY_COUNT,
      retryDelaysJson: JSON.stringify(c.retry_delays_days ?? DEFAULT_RETRY_DELAYS_DAYS),
      briefJson: JSON.stringify(briefValue),
      goalText: c.campaign_goals ?? '',
      kpisJson: JSON.stringify(kpisValue),
      outputSchemaJson: JSON.stringify(schemaValue),
    };
  }, []);

  // LT-532 — keep the per-retry delays array length in sync with the count.
  const handleRetryCountChange = useCallback((next: number) => {
    setRetryCount(next);
    setRetryDelaysDays((prev) => resizeRetryDelays(prev, next));
  }, []);

  const handleRetryDelayChange = useCallback((index: number, value: number) => {
    setRetryDelaysDays((prev) => prev.map((d, i) => (i === index ? value : d)));
  }, []);

  // LT-534 — materialise picked contacts as a static list and select it as
  // the audience source. Reuses the existing contact_list_id launch path.
  const handlePickedContacts = useCallback(async (contacts: Contact[]) => {
    if (contacts.length === 0) return;
    setPickerBusy(true);
    setPickerError(null);
    try {
      const list = await createStaticListFromContacts(
        pickedListName(name, contacts.length),
        contacts.map((c) => c.id),
      );
      setContactLists((prev) => [list, ...prev]);
      setContactListId(list.id);
      setPickerOpen(false);
    } catch (e) {
      setPickerError(
        e instanceof Error ? e.message : 'Failed to create list from selection',
      );
    } finally {
      setPickerBusy(false);
    }
  }, [name]);

  /** Save the form (create or patch) without navigating. Returns the saved
   * Campaign on success, null on failure (error / validation state is set
   * by this function so callers can simply ignore null).
   *
   * Shared by ``handleSubmit`` (Save button → navigates to detail page) and
   * ``handleTestAgent`` (header Save & test → opens modal in place).
   */
  const performSave = useCallback(async (): Promise<Campaign | null> => {
    setError(null);
    setValidationError(null);

    if (!name.trim()) {
      setValidationError('Name is required.');
      return null;
    }
    if (retryDelaysDays.length !== retryCount) {
      setValidationError('Retry delays must have one entry per retry.');
      return null;
    }

    let audience: Record<string, unknown> | null | undefined;
    try {
      audience = parseAudienceFilter();
    } catch (err) {
      setValidationError(err instanceof Error ? err.message : String(err));
      return null;
    }

    setSaving(true);
    try {
      if (mode === 'create') {
        const payload: CampaignCreatePayload = {
          name: name.trim(),
          // LT-532: no sequence picker — the backend auto-attaches the
          // workspace's implicit sequence (LT-528) when sequence_id is absent.
          description: description.trim() || undefined,
          enrolment_closes_at: enrolmentClosesAt
            ? new Date(enrolmentClosesAt).toISOString()
            : undefined,
          audience_filter: (audience as Record<string, unknown> | undefined) ?? undefined,
          // LT-392 — operator-selected ContactList. When set, launch
          // snapshots it; falls back to audience_filter when empty.
          contact_list_id: contactListId || undefined,
          // LT-529 — per-campaign retry policy.
          retry_count: retryCount,
          retry_delays_days: retryDelaysDays,
        };
        // LT-413: send the structured brief fields directly. ai_context
        // is now read-only (LT-409 422s ai_context writes); the bridge
        // view continues to surface the legacy shape for read.
        applyBriefToPayload(payload, brief);
        if (goalText.trim()) payload.campaign_goals = goalText.trim();
        if (kpis.length > 0) payload.kpis = kpis;
        // LT-624 — send the declared capture schema when any field exists.
        if (outputSchema.length > 0) payload.output_schema = outputSchema;
        const created = await campaignsAPI.create(payload);
        setCampaign(created);
        refreshSnapshot(created, brief, kpis, outputSchema);
        return created;
      }
      if (campaign) {
        const patch: CampaignUpdatePayload = {};
        if (can('name') && name.trim() !== campaign.name) patch.name = name.trim();
        if (can('description') && description !== (campaign.description ?? '')) {
          patch.description = description;
        }
        if (can('retry_count')) {
          if (retryCount !== campaign.retry_count) patch.retry_count = retryCount;
          if (JSON.stringify(retryDelaysDays) !== JSON.stringify(campaign.retry_delays_days)) {
            patch.retry_delays_days = retryDelaysDays;
          }
          // Backend enforces len(delays) == count; always send both when
          // either changed so the pair stays consistent.
          if (patch.retry_count !== undefined || patch.retry_delays_days !== undefined) {
            patch.retry_count = retryCount;
            patch.retry_delays_days = retryDelaysDays;
          }
        }
        if (can('enrolment_closes_at')) {
          const newIso = enrolmentClosesAt
            ? new Date(enrolmentClosesAt).toISOString()
            : undefined;
          if (newIso !== (campaign.enrolment_closes_at ?? undefined)) {
            patch.enrolment_closes_at = newIso;
          }
        }
        if (can('audience_filter')) {
          patch.audience_filter = (audience as Record<string, unknown> | undefined) ?? undefined;
        }
        if (
          can('contact_list_id')
          && contactListId !== (campaign.contact_list_id ?? '')
        ) {
          // LT-392 — empty string means "no list selected"; explicitly
          // null on the patch so the backend clears any prior value.
          patch.contact_list_id = contactListId || null;
        }
        // LT-413: structured brief fields replace ai_context on update.
        // The `can()` gate previously checked `ai_context`; we use the
        // same gate to decide whether the brief fields are editable in
        // this status (the backend's editable-field set treats them as a
        // bundle).
        if (can('ai_context')) {
          applyBriefToPayload(patch, brief);
        }
        if (can('campaign_goals')) {
          const trimmed = goalText.trim();
          if (trimmed !== (campaign.campaign_goals ?? '').trim()) {
            patch.campaign_goals = trimmed;
          }
        }
        if (can('kpis')) {
          patch.kpis = kpis;
        }
        // LT-624 — send the capture schema when editable + changed.
        if (
          can('output_schema')
          && JSON.stringify(outputSchema) !== JSON.stringify(normalizeOutputSchema(campaign.output_schema))
        ) {
          patch.output_schema = outputSchema;
        }
        const updated = await campaignsAPI.update(campaign.id, patch);
        setCampaign(updated);
        refreshSnapshot(updated, brief, kpis, outputSchema);
        return updated;
      }
      return null;
    } catch (err) {
      setError(extractCampaignApiError(err, 'Failed to save campaign.'));
      return null;
    } finally {
      setSaving(false);
    }
  }, [
    audienceFilterRaw, brief, campaign, can, contactListId, description,
    enrolmentClosesAt, goalText, kpis, outputSchema, mode, name, parseAudienceFilter,
    refreshSnapshot, retryCount, retryDelaysDays,
  ]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const saved = await performSave();
      if (!saved) return;
      navigate(`/campaigns/${saved.id}`);
    },
    [navigate, performSave],
  );

  // LT-520 — header Test Agent / Save & test. If the form is dirty, save
  // first (no navigate); on success, open the modal in place.
  const handleTestAgent = useCallback(async () => {
    if (isDirty) {
      const saved = await performSave();
      if (!saved) return;
      setTestAgentCampaignId(saved.id);
      setTestAgentOpen(true);
      return;
    }
    if (campaign) {
      setTestAgentCampaignId(campaign.id);
      setTestAgentOpen(true);
    }
  }, [campaign, isDirty, performSave]);

  const testAgentDisabledReason: string | null = (() => {
    if (saving) return 'Saving…';
    // LT-532: the campaign's implicit sequence (LT-528) always has a
    // voice_call_with_retry step, so there's nothing else to gate on. In
    // create mode the "Save & test" path persists the campaign first.
    return null;
  })();

  if (loading) {
    return (
      <PageShell>
        <div className="text-sm text-slate-500 px-3 py-6">Loading…</div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <header className="mb-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => navigate(mode === 'edit' && campaign
              ? `/campaigns/${campaign.id}`
              : '/campaigns')}
            className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200 mb-2"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1">
            {mode === 'create' ? 'New Campaign' : `Edit · ${campaign?.status}`}
          </div>
          <h1 className="text-2xl font-semibold text-slate-50">
            {mode === 'create' ? 'New Campaign' : campaign?.name ?? 'Campaign'}
          </h1>
          {isReadOnly ? (
            <p className="text-sm text-slate-400 mt-1">
              This campaign is in a terminal state and is read-only.
            </p>
          ) : null}
        </div>
        {/* LT-520 — header Test Agent button. Always rendered (even on
            terminal-status campaigns) so the operator can hear how an
            archived campaign's agent sounded. */}
        <div className="flex-shrink-0">
          <Button
            intent="secondary"
            type="button"
            onClick={handleTestAgent}
            disabled={Boolean(testAgentDisabledReason)}
            title={testAgentDisabledReason ?? undefined}
            data-testid="test-agent-button"
          >
            <Mic size={14} strokeWidth={2} />
            {isDirty ? 'Save & test' : 'Test Agent'}
          </Button>
        </div>
      </header>

      {(error || validationError) ? (
        <div className="mb-4 px-3 py-2 rounded-ds-lg border border-danger-border bg-danger-tint text-danger-text text-sm">
          {validationError ?? error}
        </div>
      ) : null}

      <Card>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-1">
          <label className="block">
            <span className="text-xs text-slate-400">Name</span>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!can('name')}
              required
              maxLength={255}
            />
          </label>

          <label className="block">
            <span className="text-xs text-slate-400">Description</span>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={!can('description')}
              rows={3}
            />
          </label>

          {/* LT-532 — Retry Strategy replaces the Sequence picker. Every
              campaign auto-attaches the workspace's implicit voice sequence
              (LT-528); the only knob the operator turns is how many times
              and how far apart to retry a no-answer (LT-529). */}
          <fieldset className="block" data-testid="retry-strategy">
            <legend className="text-xs text-slate-400">Retry Strategy</legend>
            <div className="flex flex-col gap-3 mt-1">
              <label className="block">
                <span className="text-xs text-slate-500">Number of retries</span>
                <Select
                  value={String(retryCount)}
                  onChange={(e) => handleRetryCountChange(Number(e.target.value))}
                  disabled={!can('retry_count')}
                  data-testid="retry-count"
                >
                  {[1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </Select>
              </label>
              <div className="flex flex-wrap gap-3">
                {retryDelaysDays.map((delay, i) => (
                  <label key={i} className="block">
                    <span className="text-xs text-slate-500">Retry {i + 1}</span>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={1}
                        value={String(delay)}
                        onChange={(e) => handleRetryDelayChange(i, Number(e.target.value))}
                        disabled={!can('retry_count')}
                        className="w-20"
                        data-testid={`retry-delay-${i}`}
                      />
                      <span className="text-xs text-slate-500">days</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </fieldset>

          <label className="block">
            <span className="text-xs text-slate-400">Enrolment closes at (optional)</span>
            <Input
              type="datetime-local"
              value={enrolmentClosesAt}
              onChange={(e) => setEnrolmentClosesAt(e.target.value)}
              disabled={!can('enrolment_closes_at')}
            />
          </label>

          {/* LT-392 — operator picks a Contact List as the audience source.
              When set, launch snapshots the list (so later list edits don't
              perturb in-flight enrolments). When left blank, falls through
              to the legacy audience_filter path below. */}
          <label className="block" data-testid="campaign-contact-list-selector">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-slate-400">
                Contact List (audience source)
              </span>
              {/* LT-534 — pick individual contacts; saved as a static list
                  and selected below. */}
              {can('contact_list_id') ? (
                <Button
                  intent="secondary"
                  size="sm"
                  type="button"
                  onClick={() => { setPickerError(null); setPickerOpen(true); }}
                >
                  <UserPlus size={14} strokeWidth={2} />
                  Pick contacts
                </Button>
              ) : null}
            </div>
            <Select
              value={contactListId}
              onChange={(e) => setContactListId(e.target.value)}
              disabled={!can('contact_list_id')}
            >
              <option value="">— None (use audience filter below) —</option>
              {contactLists.map((cl) => (
                <option key={cl.id} value={cl.id}>
                  {cl.name} ({cl.list_type})
                </option>
              ))}
            </Select>
            {mode === 'edit' && !can('contact_list_id') && campaign?.contact_list_id ? (
              <span className="text-xs text-slate-500 mt-1 block">
                Contact list snapshot taken at launch. Editing locked once the
                campaign goes active.
              </span>
            ) : (
              <span className="text-xs text-slate-500 mt-1 block">
                Pick a saved Contact List, or leave empty and provide a JSON
                audience filter below. At least one is required at launch.
              </span>
            )}
          </label>

          <label className="block">
            <span className="text-xs text-slate-400">
              Audience filter (JSON; matches Sequence enrollment_filter shape)
            </span>
            <Textarea
              value={audienceFilterRaw}
              onChange={(e) => setAudienceFilterRaw(e.target.value)}
              disabled={!can('audience_filter')}
              rows={5}
              spellCheck={false}
              className="font-mono text-xs"
              placeholder='{"lifecycle_stage": "qualified"}'
            />
            {mode === 'edit' && !can('audience_filter') ? (
              <span className="text-xs text-slate-500 mt-1 block">
                Audience is snapshotted at launch and cannot be edited after.
              </span>
            ) : null}
          </label>

          <CampaignGoalsKpisEditor
            goalText={goalText}
            kpis={kpis}
            disabled={!can('campaign_goals') && !can('kpis')}
            onGoalTextChange={setGoalText}
            onKpisChange={setKpis}
          />

          <CampaignOutputSchemaEditor
            schema={outputSchema}
            disabled={!can('output_schema')}
            onSchemaChange={setOutputSchema}
          />

          <CampaignBriefEditor
            value={brief}
            disabled={!can('ai_context')}
            onChange={setBrief}
          />

          <div className="flex items-center gap-2 mt-2">
            <Button intent="primary" type="submit" disabled={saving || isReadOnly}>
              <Save size={14} strokeWidth={2.5} />
              {saving ? 'Saving…' : mode === 'create' ? 'Create' : 'Save changes'}
            </Button>
            <Button
              intent="secondary"
              type="button"
              onClick={() => navigate(mode === 'edit' && campaign
                ? `/campaigns/${campaign.id}`
                : '/campaigns')}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>

      {/* LT-520 — Test Agent (WebRTC) modal. Mounted at the page level so
          the modal lives outside the form's flex flow. */}
      {testAgentOpen && testAgentCampaignId ? (
        <WebTestAgentModal
          isOpen={testAgentOpen}
          onClose={() => setTestAgentOpen(false)}
          campaignId={testAgentCampaignId}
        />
      ) : null}

      {/* LT-534 — individual-contact picker (audience source). */}
      <ContactPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={handlePickedContacts}
        title="Pick contacts for this campaign"
        description="Search and select individual contacts. They'll be saved as a static list and selected as this campaign's audience source."
        confirmLabel="Use as audience"
        busy={pickerBusy}
        error={pickerError}
      />
    </PageShell>
  );
};

export default CampaignFormPage;
